import { useSelector } from "react-redux";
import { RootState } from "../../../store/types";
import { Actions, UserType } from "../../../shared/constants";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Input, Select, Table } from "antd";
import tableUtils from "../../../utils/tableUtils";
import HrnModal from "../../../components/HrnModal";
import validateMessages, { requiredRule } from "../../../utils/validate";
import { useEffect, useState } from "react";
import { useGetServiceCentersQuery } from "../../../services/serviceCenter";
import dayjs from "dayjs";
import { useCreateOffdayMutation, useDeleteOffdayMutation, useLazyGetOffdaysQuery, useUpdateOffdayMutation } from "../../../services/offday";
import hrnUtils from "../../../utils/hrnUtils";
import useScreenSize from "../../../hooks/useScreenSize";
import dateUtils from "../../../utils/dateUtils";
const { RangePicker } = DatePicker;

const OffDays = () => {
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const { isMobile } = useScreenSize();
    const [offdayForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [editId, setEditId] = useState("");
    const [srcDateRange, setSrcDateRange] = useState<any>([dayjs().add(-1, "month"), dayjs()]);
    const [srcServiceCenterId, setSrcServiceCenterId] = useState<string | null>(null);
    const [offdays, setOffdays] = useState<any[]>([]);
    const [getOffdays, getOffdaysResult] = useLazyGetOffdaysQuery();
    const [createOffday, createOffdayResult] = useCreateOffdayMutation();
    const [updateOffday, updateOffdayResult] = useUpdateOffdayMutation();
    const [deleteOffday, deleteOffdayResult] = useDeleteOffdayMutation();
    const {
        data: serviceCenters,
        isFetching: isSCFetching,
        isSuccess: isSCSuccess,
    } = useGetServiceCentersQuery({
        companyId: loggedInUser?.currentCompany?.id
    });

    useEffect(() => {
        getOffdays({
            companyId: loggedInUser?.currentCompany?.id,
            serviceCenterId: srcServiceCenterId,
            startFrom: srcDateRange?.[0]?.format("YYYY-MM-DD"),
            endTo: srcDateRange?.[1]?.format("YYYY-MM-DD")
        }).unwrap();
    }, [loggedInUser?.currentCompany?.id, srcDateRange, srcServiceCenterId]);

    useEffect(() => {
        if (getOffdaysResult.isSuccess) {
            setOffdays(getOffdaysResult.data || []);
        }
    }, [getOffdaysResult.isFetching]);

    const showModal = () => {
        setMode(Actions.CREATE);
        setOpen(true);
    };

    const hideModal = () => {
        setOpen(false);
        offdayForm.resetFields();
        setMode("");
    };

    const onSave = async () => {
        try {
            await offdayForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = offdayForm.getFieldsValue();

        if (mode === Actions.CREATE) {
            try {
                await createOffday({
                    companyId: loggedInUser?.currentCompany?.id,
                    body: {
                        ...formFields,
                        dateRange: undefined,
                        startDate: formFields.dateRange?.[0],
                        endDate: formFields.dateRange?.[1],
                    }
                }).unwrap();
                setOpen(false);
                offdayForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateOffday({
                    companyId: loggedInUser?.currentCompany?.id,
                    id: editId,
                    body: {
                        ...formFields,
                        dateRange: undefined,
                        startDate: formFields.dateRange?.[0],
                        endDate: formFields.dateRange?.[1],
                    }
                }).unwrap();
                setOpen(false);
                offdayForm.resetFields();
            } catch (e) { }
        }
    };

    const handleEdit = (record: any) => {
        setMode(Actions.EDIT);
        setOpen(true);
        if (record.id) setEditId(record.id);
        offdayForm.setFieldsValue({
            ...record,
            dateRange: [record?.startDate && dayjs(record.startDate + "Z"), record?.endDate && dayjs(record.endDate + "Z")]
        });
    };

    const handleDelete = async (record: any) => {
        try {
            await deleteOffday({
                companyId: loggedInUser?.currentCompany?.id,
                id: record.id
            }).unwrap();
        } catch (e) { }
    };

    const columns: any[] = [
        {
            key: "title",
            header: "Title",
            className: "font-medium",
            responsive: ["xs"],
            fn: (value: any, record: any) => (<>
                <div className="text-primary">
                    {record.serviceCenterId ? (serviceCenters || []).find((x: any) => x.id === record.serviceCenterId)?.name : record.title}
                </div>
                <div><small>{dateUtils.localTime(record.startDate)?.format("DD MMM, YYYY")} - {dateUtils.localTime(record.endDate)?.format("DD MMM, YYYY")}</small></div>
            </>)
        },
        {
            key: "title",
            header: "Title",
            className: "text-primary font-medium",
            responsive: ["sm", "md", "lg", "xl", "xxl"]
        },
        {
            key: "serviceCenterId",
            header: "Service Center",
            className: "font-medium",
            responsive: ["sm", "md", "lg", "xl", "xxl"],
            fn: (value: any, record: any) => (serviceCenters || []).find((x: any) => x.id === value)?.name
        },
        {
            key: "startDate",
            header: "Start Date",
            responsive: ["sm", "md", "lg", "xl", "xxl"],
            className: "font-medium",
            fn: (value: any, record: any) => (<>{dateUtils.localTime(value)?.format("DD MMM, YYYY")}</>)
        },
        {
            key: "endDate",
            header: "End Date",
            responsive: ["sm", "md", "lg", "xl", "xxl"],
            className: "font-medium",
            fn: (value: any, record: any) => (<>{dateUtils.localTime(value)?.format("DD MMM, YYYY")}</>)
        },
        {
            key: "message",
            header: "Message",
            className: "text-primary font-medium",
            responsive: ["sm", "md", "lg", "xl", "xxl"]
        },
    ];

    return (
        <>
            <div className="flex flex-wrap justify-between items-center gap-3 pb-1">
                <div className={`text-headline ${isMobile ? "w-full" : ""}`}>Off Days</div>
                <div className="flex flex-wrap gap-2 items-center">
                    {!isMobile && <span>Date Range</span>}
                    <RangePicker
                        className={isMobile ? "w-full" : ""}
                        popupClassName="mobile-calendar"
                        format="DD/MM/YYYY"
                        size={isMobile ? "middle" : "large"}
                        value={srcDateRange}
                        onChange={setSrcDateRange}
                        showNow
                        allowClear={false}
                    />
                    {!isMobile && <span>Service Center</span>}
                    <Select
                        className={isMobile ? "w-full" : "w-[300px]"}
                        placeholder={"Service Center"}
                        options={[
                            { label: "For all service centers", value: null },
                            ...(serviceCenters || []).map((x: any) => ({ label: x.name, value: x.id }))
                        ]}
                        showSearch
                        allowClear
                        filterOption={hrnUtils.dropdownFilterOption}
                        showAction={["focus"]}
                        size={isMobile ? "middle" : "large"}
                        onChange={setSrcServiceCenterId}
                    />
                    <Button type="primary" className={`${isMobile ? "w-full" : ""}`} size={isMobile ? "middle" : "large"} onClick={showModal} >
                        <PlusOutlined /> {isMobile ? "Add Off Day" : "Add"}
                    </Button>
                </div>
            </div >
            <div>
                <Table
                    rowKey="id"
                    columns={tableUtils.buildColumns(
                        offdays,
                        columns,
                        tableUtils.editDeleteActionColumns(handleEdit, handleDelete, isMobile ? "icon" : "text")
                    )}
                    dataSource={offdays}
                    size="middle"
                    className="ant_table"
                    loading={{
                        spinning: getOffdaysResult.isFetching as boolean,
                        indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                    }}
                    pagination={false}
                />
            </div>
            <div>
                <HrnModal
                    open={open}
                    title={`${mode === Actions.CREATE ? "Add" : "Edit"} Off Day`}
                    width={520}
                    isLoading={
                        (createOffdayResult.isLoading ||
                            updateOffdayResult.isLoading) as boolean
                    }
                    onCancel={hideModal}
                    onSave={onSave}
                >
                    <div className="mt-6">
                        <Form
                            form={offdayForm}
                            layout="vertical"
                            name="offdayForm"
                            validateMessages={validateMessages}
                        >
                            <div className="mb-5">
                                <Form.Item name="title" label={<>Title<span style={{ color: "red", marginLeft: "2px" }}>*</span></>} rules={[requiredRule]}>
                                    <Input size={isMobile ? "middle" : "large"} placeholder={"Title"} />
                                </Form.Item>
                                <Form.Item name="dateRange"
                                    label={<>Date Range<span style={{ color: "red", marginLeft: "2px" }}>*</span></>}
                                    rules={[requiredRule]}
                                    initialValue={[dayjs(), dayjs()]}
                                >
                                    <RangePicker
                                        className="w-full"
                                        popupClassName="mobile-calendar"
                                        format="DD/MM/YYYY"
                                        size={isMobile ? "middle" : "large"}
                                        showNow
                                        allowClear={false}
                                    />
                                </Form.Item>
                                <Form.Item name="message" label={"Message"}>
                                    <Input size={isMobile ? "middle" : "large"} placeholder={"Message"} />
                                </Form.Item>
                                <Form.Item name="serviceCenterId" label={"Service Center"}>
                                    <Select
                                        placeholder={"Service Center"}
                                        options={[
                                            { label: "For all service centers", value: null },
                                            ...(serviceCenters || []).map((x: any) => ({ label: x.name, value: x.id }))
                                        ]}
                                        showSearch
                                        allowClear
                                        filterOption={hrnUtils.dropdownFilterOption}
                                        showAction={["focus"]}
                                        size={isMobile ? "middle" : "large"}
                                    />
                                </Form.Item>
                            </div>
                        </Form>
                    </div>
                </HrnModal>
            </div>
        </>
    );
};

export default OffDays;